import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  where,
  query,
  Timestamp,
  orderBy,
} from "firebase/firestore";
import { Event, EventType, EventLocation } from "@visitproof/types";
import { db } from "../firebase";
import { clean } from "../utils/utils";

const eventCollectionRef = collection(db, "events");

export interface CreateEventData {
  type: EventType;
  body?: string;
  subtype?: string | null;
  severity?: string;
  actionTaken?: string | null;
  images?: string[];
  location?: EventLocation;
  visitId: string;
  caseId: string;
  accountId: string;
  createdBy: string;
  /**
   * REQUIRED when the creator is a supervisor/monitor: the events create rule
   * only allows a supervisor to create an event whose supervisorId is in their
   * claims. Omitting it makes Firestore reject the write with "Missing or
   * insufficient permissions" (admins bypass this, which is why the bug only
   * showed for monitors documenting live visits).
   */
  supervisorId?: string;
}

export const EventService = {
  async createEvent(eventData: CreateEventData): Promise<string> {
    const newEvent = clean({
      ...eventData,
      subtype: eventData.subtype || null,
      severity: eventData.severity || "low",
      actionTaken: eventData.actionTaken || null,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
      updatedBy: eventData.createdBy,
    });

    const eventRef = await addDoc(eventCollectionRef, newEvent);
    return eventRef.id;
  },

  async getEvent(eventId: string): Promise<Event | null> {
    const eventDoc = await getDoc(doc(eventCollectionRef, eventId));
    if (eventDoc.exists()) {
      return { id: eventDoc.id, ...eventDoc.data() } as Event;
    }
    return null;
  },

  async getEventsByVisit(visitId: string, accountId: string): Promise<Event[]> {
    const eventSnapshot = await getDocs(
      query(
        eventCollectionRef,
        where("accountId", "==", accountId),
        where("visitId", "==", visitId),
        orderBy("createdAt", "desc")
      )
    );
    return eventSnapshot.docs.map(
      (doc) => ({ id: doc.id, ...doc.data() }) as Event
    );
  },

  /**
   * Events for a visit, scoped to one supervisor. REQUIRED for the supervisor/
   * monitor UI: the events read rule only allows a supervisor to read events
   * whose supervisorId is in their claims, so the query MUST filter by
   * supervisorId — otherwise Firestore rejects the whole query with "Missing
   * or insufficient permissions" (the error monitors hit opening a visit).
   */
  async getEventsByVisitForSupervisor(
    visitId: string,
    accountId: string,
    supervisorId: string
  ): Promise<Event[]> {
    const eventSnapshot = await getDocs(
      query(
        eventCollectionRef,
        where("accountId", "==", accountId),
        where("visitId", "==", visitId),
        where("supervisorId", "==", supervisorId),
        orderBy("createdAt", "desc")
      )
    );
    return eventSnapshot.docs.map(
      (doc) => ({ id: doc.id, ...doc.data() }) as Event
    );
  },

  /**
   * All events for a case (includes both visit-scoped and case-scoped events).
   * Used to surface phone calls logged from the mobile case screen, which have
   * a caseId but no visitId and therefore never appear in the per-visit query.
   */
  async getEventsByCase(caseId: string, accountId: string): Promise<Event[]> {
    const eventSnapshot = await getDocs(
      query(
        eventCollectionRef,
        where("accountId", "==", accountId),
        where("caseId", "==", caseId),
        orderBy("createdAt", "desc")
      )
    );
    return eventSnapshot.docs.map(
      (doc) => ({ id: doc.id, ...doc.data() }) as Event
    );
  },

  /** Phone-call events for a case, newest first. */
  async getPhoneCallsByCase(caseId: string, accountId: string): Promise<Event[]> {
    const events = await EventService.getEventsByCase(caseId, accountId);
    return events.filter((e) => e.type === "phone_call");
  },

  async updateEvent(
    eventId: string,
    updatedData: Partial<Omit<Event, "id" | "createdAt" | "createdBy">>,
    updatedBy: string
  ): Promise<void> {
    const eventDocRef = doc(eventCollectionRef, eventId);
    await updateDoc(
      eventDocRef,
      clean({
        ...updatedData,
        updatedAt: Timestamp.now(),
        updatedBy,
      })
    );
  },

  async deleteEvent(eventId: string): Promise<void> {
    const eventDocRef = doc(eventCollectionRef, eventId);
    await deleteDoc(eventDocRef);
  },
};
